import { useEffect, useRef, useCallback } from 'react';

export type SwipeDirection = 'left' | 'right' | 'up' | 'down';

export interface SwipeConfig {
  minDistance?: number;
  maxTime?: number;
  minVelocity?: number;
  touchTargetElement?: HTMLElement | null;
  preventDefault?: boolean;
}

export interface SwipeCallbacks {
  onSwipeLeft?: (event: TouchEvent) => void;
  onSwipeRight?: (event: TouchEvent) => void;
  onSwipeUp?: (event: TouchEvent) => void;
  onSwipeDown?: (event: TouchEvent) => void;
  onSwipe?: (direction: SwipeDirection, event: TouchEvent) => void;
}

interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

const DEFAULT_CONFIG: Required<SwipeConfig> = {
  minDistance: 50,
  maxTime: 500,
  minVelocity: 0.3,
  touchTargetElement: null,
  preventDefault: false,
};

export function useSwipeGesture(callbacks: SwipeCallbacks, config: SwipeConfig = {}) {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const touchStartRef = useRef<TouchPosition | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);

  const calculateDirection = useCallback(
    (startPos: TouchPosition, endPos: TouchPosition): SwipeDirection | null => {
      const deltaX = endPos.x - startPos.x;
      const deltaY = endPos.y - startPos.y;
      const deltaTime = endPos.time - startPos.time;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const velocity = distance / deltaTime;

      // Check if swipe meets minimum requirements
      if (
        distance < finalConfig.minDistance ||
        deltaTime > finalConfig.maxTime ||
        velocity < finalConfig.minVelocity
      ) {
        return null;
      }

      // Determine primary direction
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY) {
        return deltaX > 0 ? 'right' : 'left';
      } else {
        return deltaY > 0 ? 'down' : 'up';
      }
    },
    [finalConfig.minDistance, finalConfig.maxTime, finalConfig.minVelocity]
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (finalConfig.preventDefault) {
        event.preventDefault();
      }

      const touch = event.touches[0];
      if (touch) {
        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now(),
        };
      }
    },
    [finalConfig.preventDefault]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!touchStartRef.current) return;

      if (finalConfig.preventDefault) {
        event.preventDefault();
      }

      const touch = event.changedTouches[0];
      if (!touch) return;

      const endPos: TouchPosition = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };

      const direction = calculateDirection(touchStartRef.current, endPos);

      if (direction) {
        // Call direction-specific callback
        switch (direction) {
          case 'left':
            callbacks.onSwipeLeft?.(event);
            break;
          case 'right':
            callbacks.onSwipeRight?.(event);
            break;
          case 'up':
            callbacks.onSwipeUp?.(event);
            break;
          case 'down':
            callbacks.onSwipeDown?.(event);
            break;
        }

        // Call general swipe callback
        callbacks.onSwipe?.(direction, event);
      }

      touchStartRef.current = null;
    },
    [callbacks, calculateDirection, finalConfig.preventDefault]
  );

  useEffect(() => {
    const targetElement = finalConfig.touchTargetElement || elementRef.current;

    if (!targetElement) return;

    const options = { passive: !finalConfig.preventDefault };

    targetElement.addEventListener('touchstart', handleTouchStart, options);
    targetElement.addEventListener('touchend', handleTouchEnd, options);

    return () => {
      targetElement.removeEventListener('touchstart', handleTouchStart);
      targetElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    handleTouchStart,
    handleTouchEnd,
    finalConfig.touchTargetElement,
    finalConfig.preventDefault,
  ]);

  return elementRef;
}
